import {$qsAll} from '../../../../../@shared/ts/dom';
import {type StripeGateway, type IStripeContext} from '../../../models/stripe';
import {type CreatePaymentMethodSepaDebitData} from '@stripe/stripe-js';
import {PeachPayCustomer} from '../../../reducers/peachPayCustomerReducer';
import {getLocaleText} from '../../../util/translation';
import bankImageURL from '../../../../img/marks/bank.svg';
import {FeatureFlag} from '../../../../../@type/features';
import {Feature} from '../../../reducers/environmentReducer';
import {createSetupIntent} from '../setup-intent';
import {DefaultCart} from '../../../reducers/cartReducer';
import {type ConfirmResult} from '../../../../../@type/stripe';

// https://stripe.com/docs/payments/sepa-debit/accept-a-payment
function initStripeSepaDebitMethod(): StripeGateway {
	const sepaDebitDescription = /* html */ `<p style="margin: 0;font-size: smaller; text-align: justify">${getLocaleText('After clicking <b>Pay</b> your payment will be submitted immediately.')}</p>
	<hr><p class="muted" style="margin: 0;">${getLocaleText('Payments typically take 5 days to complete.')}
	${getLocaleText('By providing your IBAN and confirming this payment, you are authorizing ') + (window.top?.location.hostname ?? '') + getLocaleText(' and Stripe, our payment service provider, to send instructions to your bank to debit your account and your bank to debit your account in accordance with those instructions. You are entitled to a refund from your bank under the terms and conditions of your agreement with your bank. A refund must be claimed within 8 weeks starting from the date on which your account was debited.')}</p>`;

	return {
		config: {
			name: Feature.metadata<string>(FeatureFlag.STRIPE_SEPADEBIT_GATEWAY, 'title') ?? getLocaleText('SEPA Debit'),
			description: sepaDebitDescription,
			gatewayId: 'peachpay_stripe_sepadebit',
			assets: {
				title: {src: bankImageURL},
				badge: {src: bankImageURL},
			},
		},
		init(context: IStripeContext) {
			const {elements} = context;

			const options = {
				style: {
					base: {
						color: '#32325d',
						fontSize: '16px',
					},
				},

				supportedCountries: ['SEPA'],
			};

			const $iban = elements.create('iban', options);

			const description = /* html */ `<div class="pp-stripe-sepadebit-iban-mount"></div><hr>${sepaDebitDescription}</div>`;

			$qsAll('div.pp-pm-saved-option[data-gateway="peachpay_stripe_sepadebit"]', $el => {
				$el.innerHTML = description;
			});

			$iban.mount('#pp-pms-new div.pp-pm-saved-option[data-gateway="peachpay_stripe_sepadebit"] div.pp-stripe-sepadebit-iban-mount');
		},
		async tokenize(context: IStripeContext) {
			const {stripe, elements} = context;

			const $iban = elements.getElement('iban');
			if (!$iban) {
				return [null, {
					paymentStatus: 'creationfailed',
					note: 'Failed to find IBAN element.',
				}];
			}

			const billingDetails = PeachPayCustomer.stripeBillingDetails() as CreatePaymentMethodSepaDebitData['billing_details'];

			if (!billingDetails.email || billingDetails.name === ' ') {
				return [null, {
					paymentStatus: 'creationfailed',
					note: 'Billing name and billing email are required fields.',
				}];
			}

			const {paymentMethod, error} = await stripe.createPaymentMethod({
				type: 'sepa_debit',
				sepa_debit: $iban,
				billing_details: billingDetails,
			});

			if (error) {
				const message = error.message ?? 'Failed to tokenize payment method.';

				return [null, {
					paymentStatus: 'creationfailed',
					note: message,
				}];
			}

			if (!paymentMethod?.id) {
				return [null, {
					paymentStatus: 'creationfailed',
					note: 'Failed to tokenize payment method.',
				}];
			}

			// Check for a cart total of 0. If it's more than zero, we do not need setupIntent.
			// See https://www.notion.so/peachpay/Stripe-3D-Secure-card-payments-sometimes-fail-01fae4e0b8694e3680f8d6f6733770df.
			if (DefaultCart.total() > 0) {
				return [paymentMethod.id, {}];
			}

			const clientSecret = await createSetupIntent('sepa_debit');
			if (!clientSecret) {
				return [null, {
					paymentStatus: 'creationfailed',
					note: 'Failed to create setup intent.',
				}];
			}

			const {error: confirmError, setupIntent: confirmIntent} = await stripe.confirmSepaDebitSetup(
				clientSecret,
				{
					payment_method: {
						sepa_debit: $iban,
						billing_details: {
							name: billingDetails.name,
							email: billingDetails.email,
						},
					},
				},
			);

			if (!confirmIntent || confirmError) {
				return [null, {
					paymentStatus: 'creationfailed',
					note: confirmError?.message ?? 'Failed to confirm IBAN setup intent.',
				}];
			}

			if (confirmIntent.status !== 'succeeded') {
				return [null, {
					paymentStatus: 'creationfailed',
					note: `Setup intent resulted in invalid status(${confirmIntent.status})`,
				}];
			}

			return [confirmIntent.payment_method as string, {}];
		},
		async confirm(context: IStripeContext, clientSecret: string): Promise<ConfirmResult> {
			const {stripe} = context;
			const {paymentIntent, error} = await stripe.retrievePaymentIntent(clientSecret);

			if (!paymentIntent || error) {
				const message = error?.message ?? 'Failed to retrieve payment intent.';
				return [null, {
					note: message,
				}];
			}

			if (paymentIntent.status === 'succeeded'
				|| paymentIntent.status === 'requires_capture'
				|| paymentIntent.status === 'processing') {
				return ['success', {}];
			}

			if (paymentIntent.status === 'requires_action') {
				const {paymentIntent, error} = await stripe.confirmSepaDebitPayment(clientSecret);
				if (!paymentIntent || error) {
					const message = error?.message ?? 'Failed to confirm payment intent.';
					return [null, {
						note: message,
					}];
				}

				if (paymentIntent.status === 'succeeded'
					|| paymentIntent.status === 'requires_capture'
					|| paymentIntent.status === 'processing') {
					return ['success', {}];
				}
			}

			return [null, {
				note: `Failed to confirm payment intent with status(${paymentIntent.status})`,
			}];
		},
	};
}

export {initStripeSepaDebitMethod};
